import {useCallback, useEffect, useState} from "react"
import {RetailersGatewayProviderConfig} from "hooks/usePaymentProviderConfigs"
import {RetailersGatewayPaymentDetails, StudentPaymentMethodType} from "types/student-account/student-payment"

declare global {
    interface Window {
        CollectJS?: any
    }
}

export type RetailersGatewayPaymentType = "card" | "ach"

type CollectJsResponse = {
    token: string
    card?: {number?: string | null; exp?: string | null; type?: string | null} | null
    check?: {name?: string | null; account?: string | null; aba?: string | null} | null
}

const SCRIPT_ID = "retailers-gateway-collectjs"
// Collect.js binds its lightbox to a page element by default (#payButton) — point it at one that
// never exists so it only opens when startPaymentRequest() is called explicitly.
const UNUSED_PAYMENT_SELECTOR = "#retailers-gateway-collectjs-trigger"

let loadingPromise: {key: string; promise: Promise<void>} | null = null

const loadCollectJs = (gatewayUrl: string, tokenizationKey: string): Promise<void> => {
    if (loadingPromise?.key === tokenizationKey) return loadingPromise.promise

    // A different tokenization key (another campus' gateway account) needs a fresh script.
    document.getElementById(SCRIPT_ID)?.remove()
    delete window.CollectJS

    const promise = new Promise<void>((resolve, reject) => {
        const script = document.createElement("script")
        script.id = SCRIPT_ID
        script.src = `${gatewayUrl}/token/Collect.js`
        script.async = true
        script.setAttribute("data-tokenization-key", tokenizationKey)
        script.setAttribute("data-variant", "lightbox")
        script.setAttribute("data-payment-selector", UNUSED_PAYMENT_SELECTOR)
        script.onload = () => resolve()
        script.onerror = () => {
            loadingPromise = null
            reject(new Error("Failed to load the payment form. Please try again."))
        }
        document.body.appendChild(script)
    })
    loadingPromise = {key: tokenizationKey, promise}
    return promise
}

const toPaymentDetails = (
    type: RetailersGatewayPaymentType,
    response: CollectJsResponse
): RetailersGatewayPaymentDetails => {
    if (type === "ach") {
        return {
            type: StudentPaymentMethodType.Ach,
            last4: response.check?.account?.slice(-4) ?? null,
            holderName: response.check?.name ?? null,
            bankRoutingNumber: response.check?.aba ?? null
        }
    }
    const exp = response.card?.exp ?? ""
    return {
        type: StudentPaymentMethodType.Card,
        last4: response.card?.number?.slice(-4) ?? null,
        cardBrand: response.card?.type ?? null,
        expMonth: exp.length === 4 ? Number(exp.slice(0, 2)) : null,
        expYear: exp.length === 4 ? 2000 + Number(exp.slice(2)) : null
    }
}

/**
 * Collect.js (Retailers Gateway / NMI) lightbox: the gateway-hosted pop-up collects the card or
 * bank account and returns a single-use payment token, so no payment data touches our pages.
 */
export const useRetailersGatewayCollect = (config?: RetailersGatewayProviderConfig) => {
    const [isReady, setIsReady] = useState(false)
    const [error, setError] = useState<Error | null>(null)

    useEffect(() => {
        if (!config?.tokenizationKey || !config.gatewayUrl) return
        let isMounted = true
        setIsReady(false)
        loadCollectJs(config.gatewayUrl, config.tokenizationKey)
            .then(() => isMounted && setIsReady(!!window.CollectJS))
            .catch((loadError) => isMounted && setError(loadError))
        return () => {
            isMounted = false
        }
    }, [config?.tokenizationKey, config?.gatewayUrl])

    /**
     * Opens the lightbox. onToken only fires once the student submits valid details — closing the
     * lightbox fires nothing, so callers must not show a loading state until onToken runs.
     */
    const openPaymentForm = useCallback(
        (
            type: RetailersGatewayPaymentType,
            onToken: (paymentToken: string, paymentDetails: RetailersGatewayPaymentDetails) => void
        ) => {
            if (!window.CollectJS) {
                throw new Error("The payment form is still loading. Please try again in a moment.")
            }
            window.CollectJS.configure({
                variant: "lightbox",
                paymentSelector: UNUSED_PAYMENT_SELECTOR,
                paymentType: type === "ach" ? "ck" : "cc",
                callback: (response: CollectJsResponse) => onToken(response.token, toPaymentDetails(type, response))
            })
            window.CollectJS.startPaymentRequest()
        },
        []
    )

    return {isReady, error, openPaymentForm}
}
